import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthService } from '@plugins/client/auth';
import { MultilanguageService } from '@plugins/client/multilanguage/multilanguage.service';
import { DEFAULT_FILTER_OPTIONS } from '@plugins/client/post/constants';
import { TranslateService } from '@plugins/client/translate/services';
import {
  FullQuestionDto,
  CreateQuestion,
  DiscoverQuestionList,
  QuestionDto,
  QuestionListDto,
  UpdateQuestion,
  QuestionWithFacetsDto,
  VoteDto,
} from '@plugins/faq-lib/types';
import { Subscription } from '@plugins/shared/types';
import { Filter } from '@plugins/shared/types/post/filter';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private multilanguageService = inject(MultilanguageService);

  load(filter: Filter, skip = 0, take: number = environment.questionPageSize) {
    return this.http.get<QuestionListDto>('/api/question/list', {
      params: this.questionParams(filter, skip, take),
    });
  }

  discover(data: { search: string; skip: number; take: number }) {
    const params = new HttpParams({
      fromObject: data,
    });
    return this.http.get<DiscoverQuestionList>('/api/question/discover', {
      params,
    });
  }

  export(anonymousMode = false, profileFields?: string[]) {
    let params = new HttpParams().set('anonymousMode', String(anonymousMode));
    if (profileFields && profileFields.length > 0) {
      params = params.set('profileFields', profileFields.join(','));
    }

    const language = this.translateService.currentLanguage;
    const locale = this.multilanguageService.currentLanguage();
    const headers = new HttpHeaders()
      .append('Content-Type', 'text/plain; charset=utf-8')
      .append('Accept-Language', language)
      .append('X-Content-Language', locale);

    return this.http
      .get('/api/question/export', {
        headers,
        params,
        responseType: 'text',
      })
      .pipe(
        tap((data) => this.downloadExport(data)),
        catchError(() => EMPTY),
      );
  }

  private downloadExport(data: string) {
    const fileName = `questions_${this.authService.userToken.instance_id}.csv`;
    const blob = new Blob(['\ufeff' + data], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  get(questionId: string) {
    return this.http.get<QuestionWithFacetsDto>(
      `/api/question/single/${questionId}`,
    );
  }

  getDetails(
    questionId: string,
    skipAnswers = 0,
    takeAnswers: number = environment.answerPageSize,
  ) {
    return this.http.get<FullQuestionDto>(
      `/api/question/single/${questionId}/details`,
      {
        params: new HttpParams({
          fromObject: {
            skipAnswers: skipAnswers?.toString() ?? '0',
            takeAnswers:
              takeAnswers?.toString() ?? environment.answerPageSize.toString(),
          },
        }),
      },
    );
  }

  create(question: CreateQuestion) {
    return this.http.post<QuestionDto>('/api/question', question);
  }

  update(question: UpdateQuestion) {
    return this.http.patch<QuestionWithFacetsDto>('/api/question', question);
  }

  like(questionId: string) {
    return this.http.post<VoteDto>(`/api/question/${questionId}/like`, null);
  }

  delete(questionId: string) {
    return this.http.delete<void>(`/api/question/delete/single/${questionId}`);
  }

  deleteFiltered(filter: Filter) {
    const params = this.questionParams(filter, 0, Number.MAX_SAFE_INTEGER);
    return this.http.delete<void>(`/api/question/delete/filtered`, {
      params,
    });
  }

  deleteAll() {
    return this.http.delete<void>(`/api/question/delete/all`);
  }

  getHasQuestions() {
    return this.http.get<boolean>(`/api/question/hasRecords`);
  }

  private questionParams(
    filter: Filter,
    skip: number,
    take: number,
    rest: Record<string, string> = {},
  ) {
    filter = this.transformFilter(filter);
    let params = new HttpParams({
      fromObject: {
        sort: filter.sort,
        q: filter.search,
        skip: skip.toString(),
        take: take.toString(),
        ...rest,
      },
    });
    const filters = filter.filter.split(',');
    const facets = [];
    filters.forEach((fItem) => {
      const [key, val] = fItem.split('=');
      switch (key) {
        case 'facet':
          facets.push(val);
          break;
        case 'status':
          if (val !== DEFAULT_FILTER_OPTIONS[0].value) {
            params = params.append('status', val.toString());
          }
          break;
        case 'own':
          params = params.append('own', 'true');
          break;
        case 'subscriptions':
          params = params.append('subscriptions', 'true');
          break;
        default:
          break;
      }
    });
    if (facets.length) {
      params = params.append('filter', facets.toString());
    }
    return params;
  }

  transformFilter(filter: Filter) {
    let { search } = filter;
    search = search.trim();
    search = search.length > 2 ? search : '';
    return { ...filter, search };
  }

  subscribe(questionId: string) {
    return this.http.get<Subscription>(`/api/question/${questionId}/subscribe`);
  }

  unsubscribe(questionId: string) {
    return this.http.get<Subscription>(
      `/api/question/${questionId}/unsubscribe`,
    );
  }
}
